<template>
  <div class="kanban-application">
    <div class="kanban-wrapper" :class="{ 'kanban-wrapper--team': teamMode }">
      <div class="kanban-container" :class="teamMode ? 'kanban-container--team' : 'kanban-container--status d-flex'">
        <div
          v-for="col in columns"
          :key="col.key"
          class="kanban-board"
          :class="teamMode ? 'kanban-board--team' : 'kanban-board--status mr-2'"
        >
          <div class="kanban-board-header d-flex align-items-center justify-content-between" :class="headerClassFor(col)">
            <div class="kanban-title-board d-flex align-items-center">
              <span class="kanban-title-icon-wrap" :class="`kanban-icon--${col.variant}`">
                <feather-icon :icon="col.icon" size="14" />
              </span>
              <span
                class="kanban-title-text"
                :class="teamMode ? 'kanban-title-text--team' : 'text-truncate'"
              >{{ col.label }}</span>
              <b-badge pill :variant="`light-${col.variant}`" class="kanban-title-count">
                {{ (localColumns[col.key] || []).length }}
              </b-badge>
            </div>
            <b-button
              v-if="teamMode"
              :aria-label="`Add task for ${col.label}`"
              variant="flat-primary"
              size="sm"
              class="btn-icon kanban-add-btn"
              @click="$emit('quick-add', col.key)"
            >
              <feather-icon icon="PlusIcon" size="16" />
            </b-button>
          </div>

          <!--
            Cards render as DIRECT children of `draggable` — do NOT wrap
            them in a `<transition-group>`. `draggable` already delegates
            child positioning to SortableJS, which directly manipulates
            these DOM nodes on every drag; a Vue `<transition-group>`
            wrapped around the same nodes runs its own independent FLIP
            (measure-old-position / apply-change / measure-new-position /
            animate-via-transform) reconciliation on every re-render. Since
            `localColumns[col.key]` is rebuilt (new array) on every task
            change anywhere — not just in this column, `tasks` is a `deep`
            watch — the two systems raced for control of the same elements
            and produced exactly the reported symptoms: stray headerless
            cards, a card transform-displaced outside its column, page
            content appearing to overlap the footer. Removing the
            transition-group (previously here) fixes this; SortableJS's
            own drag animation is sufficient UX.
          -->
          <!--
            The scroll/height constraint for tall team columns lives on
            THIS wrapper, OUTSIDE `draggable` — not on `draggable`'s own
            root element. `draggable` hands its root node straight to
            SortableJS, which measures/positions children via
            `getBoundingClientRect()` against that exact element; giving
            that same element a clipped `overflow` + `max-height`
            simultaneously is a known source of drag/measurement glitches
            (stray/duplicated-looking cards, phantom scrollbars) because
            Sortable's drag math and the browser's own scroll clipping end
            up fighting over the same box. Constraining a plain wrapper
            around `draggable` instead avoids that entirely — `draggable`
            itself stays a totally unconstrained flex column.
          -->
          <div class="kanban-board-scroll" :class="{ 'kanban-board-scroll--team': teamMode }">
            <draggable
              :list="localColumns[col.key]"
              :group="dragGroup"
              :animation="200"
              class="kanban-drag"
              ghost-class="kanban-ghost"
              :move="evt => onMove(evt, col.key)"
              @change="evt => onChange(evt, col.key)"
            >
              <task-card
                v-for="task in localColumns[col.key]"
                :key="taskKey(task)"
                :task="task"
                :can-edit="canEditTask(task)"
                :can-delete="canDeleteTask(task)"
                :can-change-priority="canChangePriorityTask(task)"
                :can-change-status="canChangeStatusTask(task)"
                :show-status="teamMode"
                :all-tasks="allTasks"
                :team-members="teamMembers"
                :ping-revision="pingRevision"
                @view="$emit('view', task)"
                @edit="$emit('edit', task)"
                @delete="$emit('delete', task)"
                @change-priority="$emit('change-priority', $event)"
                @ping="$emit('ping', $event)"
              />
              <div v-if="!(localColumns[col.key] || []).length" class="kanban-empty-state">
                <feather-icon :icon="col.icon" size="26" class="kanban-empty-icon" />
                <p class="kanban-empty-title">No tasks</p>
                <p class="kanban-empty-hint">{{ emptyHintFor(col) }}</p>
              </div>
            </draggable>

            <!--
              A SIBLING of `<draggable>`, not a child — SortableJS treats
              every direct child of `draggable`'s root as a draggable item
              by default, so anything meant to stay put (never drag,
              never accept a drop) has to live outside that root entirely,
              same reasoning as the header's own "+" button above. This
              gives each employee's list its own always-visible "add task
              here" affordance at the bottom, in addition to the header
              button, without SortableJS ever seeing it.
            -->
            <button
              v-if="teamMode"
              type="button"
              class="kanban-quick-add-card"
              @click="$emit('quick-add', col.key)"
            >
              <feather-icon icon="PlusIcon" size="14" class="mr-50" />Add task
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { BBadge, BButton } from 'bootstrap-vue'
import TaskCard from './TaskCard.vue'
import { TASK_STATUS_COLUMNS, taskId, normalizeTaskStatus, taskAssigneeEmpId } from '@/utils/task-config'
import { sortTasksForColumn, setColumnOrder } from '@/utils/task-order-store'
import {
  canEditTaskDetails,
  canChangeStatus,
  canChangePriority,
  canDeleteTask,
  canReassignTask,
} from '@/utils/task-permissions'
import {
  canChangeTaskStatusInProject,
  hasAllowedStatusTransition,
} from '@/utils/project-permissions'
import { projectId as getProjectId } from '@/utils/project-config'

export default {
  name: 'TaskKanbanBoard',
  components: { draggable, TaskCard, BBadge, BButton },
  props: {
    tasks: { type: Array, default: () => [] },
    /** Full unscoped task list, unrelated to the column-grouping `tasks` above — passed straight through to every `<task-card>` for its own `groupId`-sibling lookup; see `TaskCard.vue`'s `allTasks` prop doc. */
    allTasks: { type: Array, default: () => [] },
    currentUser: { type: Object, default: () => ({}) },
    teamMembers: { type: Array, default: () => [] },
    /** "My Team's Tasks" mode: one column per direct report instead of per status. */
    teamMode: { type: Boolean, default: false },
    /** Bumped by `TaskBoard` after each Ping so card badges re-read the ping store. */
    pingRevision: { type: Number, default: 0 },
    /** All visible projects — for project-scoped status transition checks. */
    projects: { type: Array, default: () => [] },
  },
  data() {
    return {
      statusColumns: TASK_STATUS_COLUMNS,
      dragGroup: 'sattva-tasks-board',
      localColumns: {},
    }
  },
  computed: {
    /** Column definitions currently in play — per-status normally, per-team-member in team mode. */
    columns() {
      if (this.teamMode) {
        const cols = this.teamMembers.map(member => ({
          key: this.memberEmpId(member),
          label: this.memberName(member),
          icon: 'UserIcon',
          variant: 'primary',
        }))
        return cols
      }
      return this.statusColumns
    },
  },
  watch: {
    tasks: {
      immediate: true,
      deep: true,
      handler() {
        this.rebuildColumns()
      },
    },
    teamMode() {
      this.rebuildColumns()
    },
    teamMembers() {
      if (this.teamMode) this.rebuildColumns()
    },
  },
  methods: {
    taskKey(task) {
      return taskId(task) ?? task.title
    },
    memberEmpId(member) {
      return String(member.emp_id ?? member.id)
    },
    memberName(member) {
      if (member.first_name) return `${member.first_name} ${member.last_name || ''}`.trim()
      return member.name || member.title || this.memberEmpId(member)
    },
    headerClassFor(col) {
      return col.variant ? `kanban-header--${col.variant}` : ''
    },
    emptyHintFor(col) {
      if (this.teamMode) {
        return `Drag tasks here or use Add task for ${col.label}`
      }
      const hints = {
        'To Do': 'Tasks waiting to be started appear here',
        'In Progress': 'Active work in progress shows here',
        Review: 'Tasks ready for review land here',
        Done: 'Completed tasks move here',
      }
      return hints[col.label] || 'Drag tasks here to organize'
    },
    /** Which column a task currently belongs to, depending on board mode. */
    columnKeyForTask(task) {
      return this.teamMode ? taskAssigneeEmpId(task) : normalizeTaskStatus(task)
    },
    /**
     * Rebuilds `localColumns` from the `tasks` prop — but mutates each
     * column's EXISTING array in place (`splice`) rather than assigning a
     * brand-new object/arrays to `this.localColumns`. `<draggable>` is
     * bound via `:list="localColumns[col.key]"` and keeps its own internal
     * reference to that exact array instance; swapping in a whole new
     * array out from under it (the previous implementation did
     * `this.localColumns = grouped`, a fresh object every time) is a
     * documented vuedraggable footgun — if this watcher-driven rebuild
     * ever fires while a drag is in flight or immediately after one
     * (`tasks` is a `deep` watch, so ANY task mutation anywhere, not just
     * in this column, re-triggers it), `<draggable>`'s cached reference
     * goes stale relative to the newly-swapped-in array the template now
     * renders, and its next internal splice can land on the wrong array
     * entirely — producing exactly the "looks like it swapped two cards
     * instead of inserting" symptom reported. Mutating existing arrays in
     * place keeps every `<draggable>` instance's reference valid across
     * rebuilds, regardless of what else triggered one.
     */
    rebuildColumns() {
      const grouped = {}
      this.columns.forEach(col => { grouped[col.key] = [] })
      this.tasks.forEach(task => {
        const key = this.columnKeyForTask(task)
        // In team mode a task not assigned to one of the rendered member
        // columns (e.g. the manager's own task) has nowhere to render —
        // dropped from the board rather than crashing; see final report.
        if (key == null || !grouped[key]) return
        grouped[key].push(task)
      })
      Object.keys(grouped).forEach(key => {
        const sorted = sortTasksForColumn(grouped[key])
        if (!Array.isArray(this.localColumns[key])) {
          this.$set(this.localColumns, key, sorted)
        } else {
          this.localColumns[key].splice(0, this.localColumns[key].length, ...sorted)
        }
      })
      // Drop columns that no longer exist (e.g. a team member removed from
      // `teamMembers`) — nothing currently holds a `<draggable>` reference
      // to them once they're gone from `columns`, so a clean delete is safe.
      Object.keys(this.localColumns).forEach(key => {
        if (!grouped[key]) this.$delete(this.localColumns, key)
      })
    },
    canEditTask(task) {
      return canEditTaskDetails(this.currentUser, task)
    },
    canDeleteTask(task) {
      return canDeleteTask(this.currentUser, task)
    },
    canChangePriorityTask(task) {
      return canChangePriority(this.currentUser, task, this.teamMembers)
    },
    canChangeStatusTask(task) {
      if (!canChangeStatus(this.currentUser, task)) return false
      const project = this.projectForTask(task)
      if (!project) return true
      return hasAllowedStatusTransition(this.currentUser, project, task, this.statusColumns)
    },
    projectForTask(task) {
      const pid = task?.projectId ?? task?.project_id
      if (pid == null) return null
      return this.projects.find(p => String(getProjectId(p)) === String(pid)) || null
    },
    canChangeToStatus(task, targetStatus) {
      if (!canChangeStatus(this.currentUser, task)) return false
      const project = this.projectForTask(task)
      if (!project) return true
      return canChangeTaskStatusInProject(this.currentUser, project, task, targetStatus)
    },
    /**
     * Validates a potential drag BEFORE the drop happens.
     *
     * Status mode: within-column re-ordering needs priority-change
     * permission; cross-column drags need status-change permission.
     *
     * Team mode: within-column (same assignee) re-ordering ALSO ranks by
     * priority, so it needs the same priority-change permission; cross-
     * column drags reassign the task to the target member, gated by
     * `canReassignTask` (manager-only, target must be a valid direct
     * report/self).
     */
    onMove(evt, targetColumnKey) {
      const task = evt.draggedContext?.element
      if (!task) return false
      const sourceColumnKey = this.columnKeyForTask(task)
      if (sourceColumnKey === targetColumnKey) {
        return canChangePriority(this.currentUser, task, this.teamMembers)
      }
      if (this.teamMode) {
        return canReassignTask(this.currentUser, task, this.teamMembers, targetColumnKey)
      }
      return this.canChangeToStatus(task, targetColumnKey)
    },
    onChange(evt, columnKey) {
      if (evt.added) {
        const task = evt.added.element
        if (this.teamMode) {
          if (!canReassignTask(this.currentUser, task, this.teamMembers, columnKey)) {
            // Shouldn't happen (onMove should have blocked it), but guard anyway.
            this.rebuildColumns()
            return
          }
          this.persistColumnOrder(columnKey)
          this.$emit('reassign', {
            task,
            newAssigneeEmpId: columnKey,
            orderedIds: (this.localColumns[columnKey] || []).map(taskId),
          })
          return
        }
        if (!this.canChangeToStatus(task, columnKey)) {
          // Shouldn't happen (onMove should have blocked it), but guard anyway.
          this.rebuildColumns()
          return
        }
        this.persistColumnOrder(columnKey)
        this.$emit('status-change', {
          task,
          newStatus: columnKey,
          orderedIds: (this.localColumns[columnKey] || []).map(taskId),
        })
      } else if (evt.moved) {
        // INSERT semantics, not swap — confirmed by tracing both halves:
        //
        // 1. `vuedraggable`'s own `list` binding already applies a proper
        //    remove-then-insert `splice` BEFORE this handler ever runs (see
        //    `updatePosition()` in vuedraggable's source: `list.splice(newIndex,
        //    0, list.splice(oldIndex, 1)[0])`). Given a column [A, B, C, D]
        //    and dragging D (index 3) to above B (index 1): step 1 removes D
        //    — `list.splice(3, 1)` → list is now [A, B, C], removed [D]; step
        //    2 inserts it at the drop index — `list.splice(1, 0, D)` → [A, D,
        //    B, C]. Nothing is swapped/overwritten — B and C both shift down
        //    by one, exactly like reordering a normal array/list.
        // 2. By the time we get here, `this.localColumns[columnKey]` IS that
        //    already-correctly-reordered [A, D, B, C] array (same reference
        //    `<draggable>` mutated) — `persistColumnOrder` below re-derives
        //    order values for EVERY task currently in the column, in its
        //    current (new) sequence, not just the two cards touched by the
        //    drag gesture: ids = [A.id, D.id, B.id, C.id] -> order 0,1,2,3
        //    respectively. Re-loading (which re-sorts purely from these
        //    persisted values, see `sortTasksForColumn` in
        //    `task-order-store.js`) reproduces [A, D, B, C] exactly.
        //
        // The reported "feels like it replaced/swapped places" was a
        // perception issue, not a data one: with no drag animation
        // (`Sortable`'s default `animation: 0`), B/C snapping instantly into
        // their shifted slots when D drops looked indistinguishable from D
        // and B trading places outright. `:animation="200"` on `<draggable>`
        // above now animates that shift so it visibly reads as an insert.
        this.persistColumnOrder(columnKey)
        this.$emit('reorder', {
          columnKey,
          orderedIds: (this.localColumns[columnKey] || []).map(taskId),
        })
      } else if (evt.removed) {
        this.persistColumnOrder(columnKey)
      }
    },
    /**
     * Re-sequences the WHOLE column, not just the two cards involved in a
     * drag — reads the column's current visual order (post-drop) and
     * assigns fresh sequential order values 0..n-1 to every task in it, so
     * every task's persisted rank always matches what's on screen.
     */
    persistColumnOrder(columnKey) {
      const ids = (this.localColumns[columnKey] || []).map(taskId).filter(id => id != null)
      setColumnOrder(ids)
    },
  },
}
</script>

<style lang="scss" scoped>
// Gives this scoped block access to `$card-border-width`/`$border-color`/
// `$card-border-radius`/`$box-shadow`/`$white` below — same import path
// (and same reasoning) already used for the same purpose in `TaskCard.vue`.
@import '~@core/scss/base/bootstrap-extended/include';

// Shared "one cohesive panel" look — applied identically to BOTH board
// modes (status columns in "My Tasks", per-member columns in "My Team's
// Tasks") via `@include` below, so the two boards can never visually drift
// apart from each other. `kanban-panel` is the OUTER column: header + card
// list together read as one bordered/shadowed block instead of a
// transparent strip. `kanban-panel-row` is what an individual `.kanban-item`
// becomes INSIDE that panel — a flat list row with just a thin bottom
// divider (none on the last row) instead of its own full card treatment
// (box-shadow/border/rounded corners/large margin), since stacking a fully
// "carded" row inside an already-carded panel is what produced the
// "several disjointed floating cards" look being fixed here. Each mode
// still applies these through its OWN fully-qualified, `!important`
// selector (needed to outrank the shared unscoped demo stylesheet — see
// the specificity notes further down for each mode) — only the
// DECLARATIONS are shared, not the selectors, since the two modes sit in
// different layout contexts (fixed 4-column flex row vs. wrapping grid).
@mixin kanban-panel {
  background: $white;
  border: $card-border-width solid $border-color;
  @include border-radius($card-border-radius);
  box-shadow: $box-shadow;
  padding: 0.5rem 0;
}
@mixin kanban-panel-row {
  margin-bottom: 0 !important;
  // Matches `.kanban-board-header`'s own `0.625rem` horizontal padding
  // (below) so a row's text and the header's text line up flush against
  // the SAME panel edge — the panel itself has no horizontal padding of
  // its own (`kanban-panel` above is vertical-only), so each row's own
  // left/right padding is what creates the inset now.
  padding-left: 0.625rem !important;
  padding-right: 0.625rem !important;
  border: none !important;
  border-bottom: 1px solid $border-color !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  &:last-child {
    border-bottom: none !important;
  }
}

// The shared demo styles (see `@core/scss/base/pages/app-kanban.scss`,
// reused as-is by `@/@core/scss/vue/apps/task.scss`) size columns to fit
// their content and cap `.kanban-title-board` at a hardcoded 13rem with
// `overflow: hidden` — fine for the original 4-status board (short, fixed
// labels), but for the per-member team board, member names of varying
// length would silently get clipped from the right (icon first, then
// name, then count badge last) whenever the header's combined content
// overflowed that cap — which is EXACTLY why some columns in the "My
// Team's Tasks" screenshot showed icon+badge and others didn't: it wasn't
// random, it was long names pushing the badge (and sometimes part of the
// name) past the clip boundary. The rules below override that cap with
// `!important` (needed to beat the shared stylesheet's more deeply-nested,
// higher-specificity selectors) so every column header renders identically
// — icon and count badge always visible, name ellipsized instead of the
// whole row being clipped.
.kanban-board-header {
  padding: 0.375rem 0.625rem !important;
  margin: 0 0.5rem;
  border-radius: calc(#{$card-border-radius} - 2px);
  flex-shrink: 0;
  min-height: 0;
  min-width: 0;
  overflow: visible;
  position: relative;
  z-index: 1;

  .kanban-title-board {
    max-width: none !important;
    overflow: visible !important;
    white-space: normal !important;
    min-width: 0;
    flex: 1 1 auto;
    // Base `app-kanban.scss` adds `padding: 0.5rem` + `font-size: 1.1rem`
    // here — stacks on top of the header's own padding and is the main
    // reason these status headers read taller than they need to.
    padding: 0 !important;
    font-size: 0.9375rem;
    line-height: 1.25;
    // The shared demo stylesheet also gives `.kanban-title-board` a
    // `background-color: $white` on `:hover`/`:focus` — leftover from the
    // original demo, where this element is an inline-editable column
    // title (click-to-rename), so a hover "you can click this" highlight
    // made sense there. Here it's just a static name/icon/badge label, so
    // that highlight has no purpose and only reads as a stray box popping
    // in behind the name on hover. Neutralized (not removed outright,
    // since the shared rule is unscoped and reused by other Kanban-style
    // pages) with the same `!important` override pattern as the rest of
    // this block.
    &:hover,
    &:focus {
      background-color: transparent !important;
    }
  }
  .kanban-title-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 0.286rem;
    margin-right: 0.375rem;
    flex-shrink: 0;

    &.kanban-icon--secondary { background: rgba(130, 134, 139, 0.12); color: #82868b; }
    &.kanban-icon--info { background: rgba(0, 207, 232, 0.12); color: #00cfe8; }
    &.kanban-icon--warning { background: rgba(255, 159, 67, 0.15); color: #ff9f43; }
    &.kanban-icon--success { background: rgba(40, 199, 111, 0.12); color: #28c76f; }
    &.kanban-icon--primary { background: rgba(115, 103, 240, 0.12); color: #7367f0; }
  }
  .kanban-title-text {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &--team {
      white-space: normal;
      line-height: 1.2;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-word;
    }
  }
  .kanban-title-count {
    flex-shrink: 0;
    font-size: 0.75rem;
    padding: 0.1rem 0.35rem;
    margin-left: 0.25rem;
  }
  .kanban-add-btn {
    flex-shrink: 0;
    padding: 0.15rem !important;
    margin-left: 0.25rem;
    min-width: 1.75rem;
  }
}

// Comfortable, consistent column sizing regardless of column count — reuses
// the SAME card width (18.55rem, see `.kanban-item`/`.kanban-drag` in
// `app-kanban.scss`) the original 4-status board already uses, but forces
// every column (`.kanban-board`) to never shrink below it and instead lets
// the whole board scroll horizontally. Without this, a per-member board
// with many team members would either squeeze every column down to fit the
// viewport (tiny, unreadable columns with the last one cut off) or overflow
// invisibly if any ancestor clips horizontal overflow.
//
// `overflow-y` was `hidden` here (never `auto`/`scroll`) with no per-column
// scroll area of its own for the STATUS board (unlike the team board's
// `.kanban-board-scroll--team`, this base rule has no such cap) — and,
// exactly like `.kanban-wrapper--team` below, this element ALSO gets a
// FIXED (not min-height) `height: calc(100vh - navbar - footer - padding)`
// from the global `.navbar-floating.footer-static .app-content
// .kanban-wrapper` rule (`@core/scss/base/core/layouts/_content.scss`,
// intended for the original single-screen 4-status board). Combined,
// `hidden` + that fixed height meant a status column taller than one
// screen (e.g. "To Do" with a dozen tasks) hard-clipped everything past
// that height with NO scrollbar and NO way to reach it — those tasks
// weren't just visually cut off, they were completely unreachable. `auto`
// (scrollable, not clipped) alone would've just moved the bug into a
// SHORT internal scrollbox instead of fixing it — `height: auto` here
// frees the wrapper to its real full content height too, the same fix
// applied to the team board's wrapper below, so the whole page grows/
// scrolls normally and every task in every column is reachable.
.kanban-wrapper {
  height: auto !important;
  min-height: 0 !important;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}
.kanban-container {
  flex-wrap: nowrap;
  align-items: flex-start;
}
.kanban-board {
  flex: 0 0 auto !important;
  min-width: 18.55rem;
  background: transparent;
}

// "My Tasks" board — always exactly 4 status columns (To Do/In Progress/
// Review/Done), unlike the team board's variable member count. The global
// `.kanban-container { width: max-content !important }` (see the big
// comment below) sizes the row to fit its columns' CONTENT width and
// nothing more, so on any viewport wider than ~4 * 18.55rem the row (and
// every column in it, since they're `flex: 0 0 auto`) stays pinned to its
// minimum width, left-aligned, leaving the reported dead space on the
// right. Fixing that here — rather than reusing the team board's
// `auto-fill`/`minmax` wrapping grid — is deliberate: with a small, FIXED
// column count you want those columns to always stretch and fill the row
// (equal-width flex items), not wrap or leave gaps between fixed-size
// tracks. `kanban-container--status`/`kanban-board--status` give this a
// dedicated, higher-specificity selector so it can't affect (or be
// affected by) the `--team` grid rules below.
.kanban-application .kanban-wrapper .kanban-container.kanban-container--status {
  width: 100% !important;
}
// Same "one cohesive panel" treatment as the team board's columns below
// (`kanban-panel` mixin, defined once near the top of this block) — the
// status column already structurally wrapped its header + `<draggable>`
// card list, it just had `background: transparent` and no border/shadow
// of its own (see the base `.kanban-board` rule above), same root cause
// as the team board had. Needs this exact fully-qualified selector chain
// (not the old bare `.kanban-board.kanban-board--status`) to out-specify
// the shared unscoped stylesheet's `.kanban-application .kanban-wrapper
// .kanban-container .kanban-board { background: transparent; }` — a bare
// 2-class scoped rule happened to never conflict with that before only
// because both set the SAME value ("transparent"); actually changing the
// value needed the same specificity fix already applied to the team
// board's panel rule.
.kanban-application .kanban-wrapper .kanban-container.kanban-container--status .kanban-board.kanban-board--status {
  flex: 1 1 0 !important;
  min-width: 18.55rem;
  @include kanban-panel;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
// The card itself (`.kanban-item`, shared with `.task-card` in TaskCard.vue)
// has a hardcoded `width: 18.55rem` in the global stylesheet — stretching
// the COLUMN above without this would just add dead space INSIDE each
// column (between the fixed-width card and its now-wider column edge)
// instead of removing it. Cards fill their column's actual width instead.
//
// Same divider-row treatment as the team board (`kanban-panel-row` mixin)
// — dials each card's own box-shadow/border/rounded-corners/large margin
// back to a flat row with a thin bottom divider, now that the column
// itself (above) is the shadowed/bordered panel.
.kanban-application .kanban-wrapper .kanban-board.kanban-board--status .kanban-item {
  width: 100% !important;
  @include kanban-panel-row;
}

// "My Team's Tasks" board — one column per team member instead of per
// status. With 6+ direct reports the single non-wrapping flex row above
// forces an ever-growing horizontal scroll strip (the exact complaint:
// "show 5 in 1 Row 4 in next row like that"). Switching this board to a
// CSS grid instead lets member columns wrap onto additional rows, scrolling
// the page vertically like every other list in the app instead of hiding
// most columns off-screen to the right.
//
// EXACTLY 4 columns per row (not a responsive auto-fill count) — a fixed
// `repeat(4, ...)` reliably gives 4-per-row on every viewport, whereas
// `auto-fill`/`minmax` depends on available width and could show 3, 5, or
// 6 depending on the window, which is what produced the "6 in a row, too
// narrow, messy" result being fixed here. The `minmax(14rem, 1fr)` floor
// is a safety net only — real column width is normally MUCH wider than
// that: content width is roughly `viewport - $menu-expanded-width (260px,
// see @core/scss/base/components/_variables.scss) - $content-padding
// (2rem each side) - the page card's own p-2 padding`, e.g. ~1560px of
// content at a 1920px viewport / ~1240px at 1600px. Divided 4 ways (minus
// 3 * 1rem gaps) that's roughly 24rem and 19rem per column respectively —
// meaningfully WIDER than the old fixed 18.55rem card width, which is also
// why cards are now stretched to fill their column below instead of
// staying pinned at that fixed width.
//
// IMPORTANT: `app-kanban.scss` (imported globally, unscoped, by
// `@core/scss/vue/apps/task.scss`) sets `.kanban-application .kanban-wrapper
// .kanban-container { display: flex; width: max-content !important; }` and
// `... .kanban-board { width: auto !important; }`. Those selectors are MORE
// SPECIFIC (3-4 chained classes) than a bare `.kanban-container--team`/
// `.kanban-board--team` scoped rule (~2), and `width: max-content` on its
// own actively defeats wrapping (it sizes the row to fit every column
// unwrapped). A first pass at this grid used plain, low-specificity rules
// here and was — provably, on inspection — silently overridden by that
// shared stylesheet, so NOTHING visually changed despite the class being
// applied correctly in the DOM. Every rule below is nested under
// `.kanban-application .kanban-wrapper` and `!important`-flagged to
// deliberately out-rank those selectors (same fix pattern as the header
// override above).
//
// `height` is overridden here too — this is the actual, confirmed cause of
// the "footer renders mid-grid" bug. `@core/scss/base/core/layouts/
// _content.scss` (global, unscoped) carries a comment literally saying
// "kanban app uses kanban-wrapper class as not having content-layout" and,
// for this app's active navbar/footer combo (`navbar.type: 'floating'`,
// `footer.type: 'static'`, see `themeConfig.js`), matches
// `.navbar-floating.footer-static .app-content .kanban-wrapper` to set a
// FIXED (not min-height) `height: calc(100vh - navbar - footer - padding)`
// — i.e. exactly one screen tall, sized for the original 4-status board
// (which fits in one screen with horizontal-only scroll, `overflow-y:
// hidden` below). Turning `overflow-y` to `visible` above without also
// freeing `height` left that fixed, too-short box in place: the multi-row
// team grid overflows past it (nothing clips it, so it still paints and
// scrolls normally) but the box's OWN reported height — which is what
// normal document flow uses to position the NEXT sibling — stays pinned
// at ~1 screen. The global `<footer>` (a normal-flow sibling rendered once,
// after the whole page, by `LayoutVertical.vue` — verified structurally
// separate from this component) lands at that short, wrong offset, i.e.
// visually "inside" the grid, while the overflowing later rows continue
// to paint and scroll below it. Freeing `height` here lets `.kanban-
// wrapper--team` grow to its real (multi-row) content height, so `.app-
// content`'s `min-height` (safe, generic sticky-footer rule) — and every
// ancestor above it — sizes correctly and the footer lands after ALL rows.
.kanban-application .kanban-wrapper.kanban-wrapper--team {
  height: auto !important;
  min-height: 0 !important;
  overflow-x: visible !important;
  overflow-y: visible !important;
}
.kanban-application .kanban-wrapper .kanban-container.kanban-container--team {
  display: grid !important;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  // Explicit (matches the browser default, but stated on purpose): each
  // grid ROW is sized to its own tallest column, independently of every
  // other row — a 4-task column can never grow into the row below it or
  // into the footer. That guarantee is what CSS grid is for; the actual
  // overlap bug reported earlier (stray headerless cards, a card floating
  // outside its column, the footer rendering mid-page) was NOT a
  // row-sizing problem at all — it was Vue's <transition-group>
  // (previously nested inside <draggable>) fighting SortableJS for
  // control of the same DOM nodes; see the template comment above
  // `<draggable>`. The SEPARATE spillover bug fixed in this same round
  // (a card + phantom horizontal scrollbar rendering outside a tall
  // column's box) is explained above `.kanban-board-scroll--team` below.
  grid-auto-rows: auto;
  gap: 1rem;
  width: 100% !important;
  // `stretch` (the grid default — explicit here since the surrounding
  // rules are already `!important`-heavy and this is easy to miss on a
  // future edit): each ROW's height is still set from its own tallest
  // column only (a 1-task column in row 1 can never be pulled taller by
  // an unrelated 4-task column in row 2 — rows size independently either
  // way, `stretch` vs `start` doesn't change that). What it DOES change is
  // how SHORTER columns *within the same row* as that tallest column
  // behave: `start` (the previous value) left them at their own natural
  // height, bottom-ragged against their taller row-mate, which is the
  // reported "uneven/messy" look. `stretch` makes every column in a row
  // fill that row's full height instead, so a row's columns always end at
  // the same bottom edge — each column's card-list area
  // (`.kanban-board-scroll--team` below) is a flex child that grows to
  // fill the extra space, using its own internal scroll if ITS content
  // still doesn't fit.
  align-items: stretch;
  padding-bottom: 1rem;
}
// One employee's header + ALL of their task cards now read as a SINGLE
// cohesive panel (bg/border/radius/shadow on the column itself) instead of
// several separately-shadowed cards floating with visible page-background
// gaps between them — the column already wrapped the header and card list
// structurally; it just had `background: transparent` and no border/
// shadow of its own, and each `.kanban-item` supplied ITS OWN full
// card-style box-shadow/border (see `.task-card` in `TaskCard.vue`) with a
// large `1.5rem` margin-bottom between them, which is what made a list of
// one person's tasks look like N disconnected floating cards rather than
// one panel containing a list. The per-card box-shadow/border/margin is
// dialed back to a thin divider below (`.kanban-item` override further
// down) now that the OUTER panel provides the "this is one card" framing,
// while the individual `<task-card>` DOM nodes and `<draggable>` are
// completely untouched — this is a pure CSS treatment change, so intra-
// column reordering and cross-column drag between employees keep working
// exactly as before.
.kanban-application .kanban-wrapper .kanban-container--team .kanban-board.kanban-board--team {
  width: 100% !important;
  min-width: 0 !important;
  max-width: 100%;
  flex: initial !important;
  @include kanban-panel;
  display: flex !important;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.kanban-application .kanban-wrapper .kanban-board.kanban-board--team .kanban-item {
  width: 100% !important;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  @include kanban-panel-row;
}
.kanban-drag {
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
  max-width: 100%;
}
// Global `app-kanban.scss` sets `.kanban-drag { min-width: 18.55rem }` —
// in the team grid that fixed floor is wider than a narrow column track and
// is the direct cause of cards bleeding into the adjacent member column.
.kanban-application .kanban-wrapper .kanban-board.kanban-board--team .kanban-drag {
  min-width: 0 !important;
}

// --- Mobile / tablet responsiveness -----------------------------------
//
// Everything above sizes both board modes for desktop: the status board is
// a fixed non-wrapping 4-column flex row (each column floored at 18.55rem,
// i.e. ~297px, so 4 of them need ~1188px+ before they stop competing for
// space) and the team board is a strict 4-track CSS grid (each track
// floored at 14rem/~224px, ~896px+ before tracks get genuinely comfortable).
// Neither floor fits a phone viewport, and even most tablets in portrait
// can't comfortably fit 4 (status) or land awkwardly between 2-3 (team).
//
// Below `lg` (992px, Bootstrap's own tablet/desktop split — matches the
// breakpoint the rest of this app's own responsive CSS already uses, e.g.
// `AttendanceStats.vue`) the team grid drops to 2 columns per row instead
// of 4 — still a grid (member columns keep wrapping, not scrolling), just
// narrower tracks. Below `md` (768px, phones and small tablets) BOTH board
// modes collapse to a single full-width column stacked vertically: the
// status board's flex row becomes a normal block flow (no more forced
// non-wrap + horizontal scroll strip — reordering/dragging across 4
// side-scrolled columns on a touchscreen is a poor combination), and the
// team grid drops to exactly 1 track. Scrolling through columns top-to-
// bottom is the one universally-understood mobile gesture; a data-dense
// operational board like this one isn't the place to invent a horizontal
// swipe-between-columns pattern instead.
@media (max-width: 991px) {
  .kanban-application .kanban-wrapper .kanban-container.kanban-container--team {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  // Status board: `nowrap` + `overflow-x: auto` (set above, desktop-only
  // intent) turns into a normal wrapping/stacking block flow — each
  // column takes the full row width and the next one flows below it,
  // exactly like the team grid does at 1 column (right below).
  .kanban-application .kanban-wrapper .kanban-container.kanban-container--status {
    flex-wrap: wrap;
  }
  .kanban-application .kanban-wrapper .kanban-container.kanban-container--status .kanban-board.kanban-board--status {
    flex: 1 1 100% !important;
    min-width: 100%;
  }
  // Team board: down to exactly 1 track — same reasoning as the `lg` step
  // above, just carried further for a phone-width viewport.
  .kanban-application .kanban-wrapper .kanban-container.kanban-container--team {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.75rem;
  }
  .kanban-board {
    min-width: 0;
  }
  .kanban-board-header {
    padding: 0.35rem 0.5rem !important;
  }
  // Bigger, thumb-friendly hit areas for the two icon-only "add task"
  // affordances — 16px of icon inside the default `btn-sm`/`padding:
  // 0.25rem` combo measures well under the ~44px touch-target guideline;
  // this widens the actual tappable box without changing how large the
  // icon itself reads.
  .kanban-add-btn {
    padding: 0.5rem !important;
    margin-left: 0.25rem;
  }
  .kanban-quick-add-card {
    padding: 0.85rem;
    width: calc(100% - 1.2rem);
    margin: 0.5rem 0.6rem 0;
  }
}
// Separates the header from its own card list WITHIN a single panel
// (`kanban-panel`/`.kanban-board--team`/`.kanban-board--status` above) -
// a plain rule line, same visual weight as the divider between individual
// cards, so EITHER board reads as one bordered block with two internal
// regions rather than two stacked boxes. Shared base rule (both modes use
// the plain `.kanban-board-scroll` class; `--team` adds its own extra
// `overflow` need below) instead of duplicating the divider per mode.
//
// Neither mode gives this element (or `.kanban-drag`/`draggable` inside
// it) a `max-height` + internal `overflow-y: auto` cap - every task in
// every column must stay immediately visible/reachable without a
// per-column scrollbar (an earlier pass had one for the team board; it
// was removed since tasks past the cap were easy to miss). Columns simply
// grow to their natural full height instead, same as the page itself.
.kanban-board-scroll {
  flex: 1 1 auto;
  border-top: 1px solid $border-color;
  margin-top: 0.375rem;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
// This used to also set `overflow-x: hidden` here (team board only,
// defensive containment against a since-fixed phantom-horizontal-scrollbar
// bug from when cards had a hardcoded width instead of today's `width:
// 100%` — see `.kanban-item` above). That's REMOVED now, not just
// redundant: per the CSS spec, setting only ONE axis's `overflow` to
// anything but `visible` silently upgrades the OTHER, unset axis's
// computed value to `auto` too — so `overflow-x: hidden` alone was quietly
// turning this into a vertically-clipping/scrollable box as well (without
// `overflow-y` ever being written anywhere), which is what produced BOTH
// the reported unwanted internal scrollbar (with its own up/down arrow
// buttons) on tall employee panels AND, more importantly, silently clipped
// the Priority dropdown's menu — `boundary="window"` on that dropdown only
// controls Popper's flip/placement math, not whether an ancestor's
// `overflow` clips its rendered DOM node. (The dropdown clipping is now
// ALSO fixed at the source — see `positionFixed` in `TaskCard.vue`/
// `TaskListView.vue` — so removing this is defense in depth, not the only
// fix, but it's the direct cause of the visible scrollbar itself and must
// go regardless.) Cards can no longer overflow their column horizontally
// (they're `width: 100%` of it), so this axis needs no clipping at all —
// no replacement rule needed; `.kanban-board-scroll--team` in the template
// is left as-is (harmless, unused by CSS now) rather than touched, since
// removing a class from the template is a template change, not a style
// one, and out of scope for this fix.
.kanban-ghost {
  opacity: 0.5;
}
// Always-visible "add a task here" affordance at the bottom of each
// employee's list, in addition to the header's own icon-only "+" button —
// a sibling of `<draggable>` (see the template comment above), so
// SortableJS never treats it as a sortable/droppable item.
.kanban-quick-add-card {
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(100% - 1.25rem);
  margin: 0.5rem 0.625rem 0.25rem;
  padding: 0.6rem;
  font-size: 0.9rem;
  color: $text-muted;
  background: transparent;
  border: 1px dashed #d8d6de;
  border-radius: $card-border-radius;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s, background-color 0.15s;
  flex-shrink: 0;

  &:hover,
  &:focus {
    color: $primary;
    border-color: $primary;
    background-color: rgba($primary, 0.04);
    outline: none;
  }
}
</style>
